import { IHolidayFull } from './holidays'
import { ILeaveRequest } from './leave-request'

export interface IBurnoutScore {
  score: number
  lastVacationDate: string
}

// Energy Score Calculation Engine Types
export enum EnergyMode {
  PEAK = 'peak', // 85 - 100
  ENERGIZED = 'energized', // 65 - 84
  FUNCTIONAL = 'functional', // 45 - 64
  STRUGGLING = 'struggling', // 25 - 44
  DRAINED = 'drained', // 0 - 24
}

export interface IEnergyScoreComponent {
  insight: string
  insights: any[]
  score: number
  pending?: number
  scheduled?: number
  daysSinceLastBreak?: number
  hasEverTakenTimeOff?: boolean
  hoursSinceLastBreak?: number
  leaveHistory?: {
    averageGapDays: number
    lastLeaveDate: string | null
    longestGapDays: number
    totalRequests: number
  }
  historicalPattern?: {
    avgRolloverPercentage: number
    maxRolloverHits: number
    trend: string
  }
  metrics?: {
    earned: number
    expiring: number
    pending: number
    remaining: number
    rollover: number
    scheduled: number
    used: number
  }
  avgRollover?: number
  trend?: string
  workLifeMetrics?: {
    averageHoursPerDay: number
    overtimeHours: number
    workLifeBalanceRatio: number
  }
}

export interface IEnergyScoreMeta {
  components: {
    accrualHealth: IEnergyScoreComponent
    leavePattern: IEnergyScoreComponent
    planning: IEnergyScoreComponent
    ptoBalance: IEnergyScoreComponent
    recentRest: IEnergyScoreComponent
    rolloverHealth: IEnergyScoreComponent
    workLifeBalance: IEnergyScoreComponent
  }
  weights?: IDynamicWeights
  insights: Array<{
    action: string
    message: string
    type: string
  }>
  recommendations: Array<{
    action: string
    impact: string
    priority: string
    timeframe: string
  }>
  rolloverPatterns: {
    avgRolloverDays: number
    avgRolloverPercentage: number
    details: Array<{
      hitMaxCap: boolean
      rolloverPercentage: number
      totalMaxRollover: number
      totalQuota: number
      totalRollover: number
      year: string
    }>
    hasHistory: boolean
    maxRolloverHits: number
    trend: string
  }
  tenure: {
    daysEmployedInCurrentYear: number
    isFirstYear: boolean
    isNewEmployee: boolean
    monthsEmployed: number
    percentOfYearEmployed: number
    startedMidYear: boolean
    yearsEmployed: number
  }
  workingHoursAvailable: boolean
  workingHoursMetrics: {
    averageHoursPerDay: number
    isPartTime: boolean
    overtimeHours: number
    totalExpectedHours: number
    totalPTOHours: number
    totalWorkedHours: number
    workLifeBalanceRatio: number
  }
}

export interface IEnergyScoreResponse {
  companyId: string
  SK: string
  algorithmVersion: string
  correlationId: string
  creator: string
  energyScore: {
    meta: IEnergyScoreMeta
    score: number
  }
  entityType: string
  insightType: string
  score: number
  timestamp: string
  userId: string
}

// Actual API response interface
export interface IEnergyLevelApiResponse {
  userId: string
  score: number
  latestEnergyScore?: IEnergyScore
  history?: Array<{
    timestamp: string
    score: number
    algorithmVersion: string
  }>
  referenceTimestamp?: string
  departmentHistory?: Array<{
    timestamp: string
    score: number
    algorithmVersion: string
  }>
  feedbackHistory?: IFeedbackSubmission[]
}

// Energy Score Calculation Engine Types
export interface IRolloverYearData {
  year: string | Date
  totalRollover: number
  totalQuota: number
  totalMaxRollover: number
  hitMaxCap: boolean
  rolloverPercentage: number
}

export interface ILeaveHistory {
  requests: ILeaveRequest[]
  lastLeaveEndDate: Date | null
  longestGapDays: number
  averageGapDays: number
  totalDaysTaken: number
}

export interface IFutureLeaveInfo {
  hasUpcomingLeave: boolean
  daysUntilNextLeave: number | null
  totalDaysScheduled: number
  nextLeaveStartDate: Date | null
  nextLeaveDuration: number | null
  upcomingRequests: ILeaveRequest[]
}

export interface IRolloverPatterns {
  hasHistory: boolean
  avgRolloverDays: number
  avgRolloverPercentage: number
  maxRolloverHits: number
  trend: 'insufficient_data' | 'stable' | 'increasing' | 'decreasing'
  details?: IRolloverYearData[]
}

export interface IInsight {
  type: 'critical' | 'warning' | 'pattern' | 'positive'
  message: string
  action: string
}

export interface IRecommendation {
  priority: 'high' | 'medium' | 'low'
  action: string
  timeframe: string
  impact: string
}

export interface IPTOTotals {
  earned: number
  earnedWithoutRollover: number
  used: number
  scheduled: number
  pending: number
  rollover: number
  quota: number
  remaining: number
  expiring: number
  hasUnlimited: boolean
  accrualType: string | null
  committed: number
  maxRollover: number
}

export interface IWorkingHoursMetrics {
  averageHoursPerDay: number
  totalExpectedHours: number
  totalWorkedHours: number
  totalPTOHours: number
  overtimeHours: number
  workLifeBalanceRatio: number
  isPartTime: boolean
}

export interface IComponentScore {
  score: number
  insight: string
  contribution?: IComponentContribution // Embed contribution data directly in component
  workingHoursImpact?: {
    hoursAdjustedScore: number
    adjustmentReason: string
  }
  [key: string]: any
}

export interface IEnergyScoreComponents {
  ptoBalance: IComponentScore
  recentRest: IComponentScore
  leavePattern: IComponentScore
  planning: IComponentScore
  accrualHealth: IComponentScore
  rolloverHealth: IComponentScore
  workLifeBalance?: IComponentScore
}

export interface IFeedbackSubmission {
  timestamp: string
  score: number
}

export interface IFeedbackModifier {
  weight: number
  feedbackScore: number
  latestFeedbackTimestamp: string
}

export interface IEnergyScoreResult {
  score: number
  components?: IEnergyScoreComponents
  weights?: IDynamicWeights
  rolloverPatterns?: IRolloverPatterns
  insights?: IInsight[]
  recommendations?: IRecommendation[]
  error?: string
  tenure?: IEmployeeTenure
  workingHoursMetrics?: IWorkingHoursMetrics
  workingHoursAvailable?: boolean
  feedbackModifier?: IFeedbackModifier
}

export interface IDynamicWeights {
  ptoBalance: number
  recentRest: number
  leavePattern: number
  planning: number
  accrualHealth: number
  rolloverHealth: number
  workLifeBalance?: number
}

export interface IComponentContribution {
  // Actual points contributed to the final score
  current: number
  // Maximum possible points for this component (when score = 100)
  max: number
}

export interface IEmployeeTenure {
  monthsEmployed: number
  yearsEmployed: number
  isNewEmployee: boolean
  isFirstYear: boolean
  startedMidYear: boolean
  daysEmployedInCurrentYear: number
  percentOfYearEmployed: number
}

// Energy Score Additional Info structure (updated to use proper types)
export interface IEnergyScoreMetaData {
  components?: IEnergyScoreComponents
  rolloverPatterns?: IRolloverPatterns
  tenure?: IEmployeeTenure
  insights?: IInsight[]
  recommendations?: IRecommendation[]
  weights?: IDynamicWeights
  feedbackModifier?: IFeedbackModifier
  error?: string
}

export interface IEnergyScore {
  score: number
  meta?: IEnergyScoreMetaData
}

export interface IUserInsightSurvey {
  completedAt: Date
  surveyId: string
  surveyName: string
  questions: object[]
}

// Energy checkin survey types
export enum EnergyCheckInType {
  PRE_VACATION = 'pre-vacation-checkin',
  POST_VACATION = 'post-vacation-checkin',
}

export interface IEnergyCheckInSurvey extends IUserInsightSurvey {
  energyCheckInType: EnergyCheckInType
  // Tuple type enforces exactly one question - energy check-ins are single-question surveys by design
  questions: [{
    questionId: 'energy-level'
    answer: EnergyMode
  }]
}

export interface IUserInsightFeedback {
  score: number
}

export interface IUserInsightData {
  burnoutScore?: IBurnoutScore
  energyScore?: IEnergyScore
  survey?: IUserInsightSurvey
  feedback?: IUserInsightFeedback
  score?: number
}

export interface IUserInsight extends IUserInsightData {
  companyId: string
  userId: string
  timestamp: string
  algorithmVersion: string
  creator: string
  correlationId: string
}

export interface IInsightPeriodConfig {
  period: InsightPeriod
  customStartDate?: string
  customEndDate?: string
  referenceDate?: Date // For calculating relative periods (e.g., previous quarter, last month)
}

export enum InsightPeriod {
  LAST_MONTH = 'LAST_MONTH',
  CURRENT_MONTH = 'CURRENT_MONTH',
  LAST_QUARTER = 'LAST_QUARTER' ,
  LAST_YEAR = 'LAST_YEAR',
  CUSTOM = 'CUSTOM'
}

export enum QueueMessageType {
  SEND_COMPANY_INSIGHTS_EMAIL = 'SEND_COMPANY_INSIGHTS_EMAIL'
}

export enum RecipientGroups {
  ADMINISTRATOR = 'administrator',
  APPROVER = 'approver'
}

export enum PTOSuggestionsContextInfo {
  REQUIREMENTS_NOT_MET = 'requirements_not_met',
  NORMAL = 'normal',
  INSUFFICIENT_BALANCE = 'insufficient_balance',
  RECENT_RETURN = 'recent_return',
  UPCOMING_LEAVE = 'upcoming_leave',
  NO_COMPELLING_REASON = 'no_compelling_reason'
}

export enum CompellingReasonType {
  EXPIRING_PTO = 'expiring_pto',
  UPCOMING_HOLIDAYS = 'upcoming_holidays',
  MAJOR_TEAM_GAP = 'major_team_gap',
  GOOD_TEAM_AVAILABILITY = 'good_team_availability',
  PLANNER_RECHARGE_TYPE = 'planner_recharge_type'
}

export enum EnergyPredictionFactorType {
  NO_TEAM_CONFLICTS = 'no_team_conflicts',
  MINIMAL_TEAM_CONFLICTS = 'minimal_team_conflicts',
  SOME_TEAM_CONFLICTS = 'some_team_conflicts',
  EXTENDED_VACATION_WITH_HOLIDAYS = 'extended_vacation_with_holidays',
  VACATION_INCLUDES_HOLIDAYS = 'vacation_includes_holidays',
  WEEK_PLUS_VACATION = 'week_plus_vacation',
  EXTENDED_WEEKEND = 'extended_weekend',
  SHORT_BREAK = 'short_break',
  ESTABLISHED_WORK_PATTERNS = 'established_work_patterns',
  DEVELOPING_WORK_PATTERNS = 'developing_work_patterns',
  NEW_EMPLOYEE_PATTERNS = 'new_employee_patterns',
  HISTORICAL_ENERGY_DATA = 'historical_energy_data',
  LIMITED_DATA = 'limited_data',
  TEMPORARILY_UNAVAILABLE = 'temporarily_unavailable',
  INSUFFICIENT_DATA = 'insufficient_data'
}

export interface ICompellingReason {
  type: CompellingReasonType
  meta?: {
    expiringAmount?: number
    holidayCount?: number
    upcomingHolidays?: IHolidayFull[]
    rechargeType?: string
  }
}

export interface IBroadcastPayload {
  type: QueueMessageType
  companyId: string
  recipients: string[]
  recipientGroups: RecipientGroups[]
  periodConfig?: IInsightPeriodConfig
}
